import * as React from 'react';
import { CSSReset } from '@chakra-ui/core';
import { css } from '@emotion/core';

import ThemeProvider from './ThemeProvider';
import createTheme from './createTheme';
import defaultTheme, { GlobalStyle } from './theme';
import { Box } from './components/box';
import reset from './base/reset';
import { colors } from './base/colors';
import { createColorScale } from './utils/colors';

export {
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Avatar,
    Badge,
    // Box,
    // Button,
    ButtonGroup,
    CSSReset,
    // Flex,
    // Heading,
    Icon,
    IconButton,
    Image,
    InputGroup,
    InputLeftAddon,
    InputRightAddon,
    InputLeftElement,
    InputRightElement,
    // Link,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuGroup,
    MenuDivider,
    MenuOptionGroup,
    MenuItemOption,
    PseudoBox,
    RadioGroup,
    Spinner,
    Stack,
    Tooltip,
    // Text,
    ThemeProvider,
    useToast
} from '@chakra-ui/core';

export { Box, Absolute, Fixed, Hide, Surface, BoxProps } from './components/box';
export { Button } from './components/button';
export { Card } from './components/card';
export { DelayedRender } from './components/render';
export { DropDownMenu, ExpandingMenu, SidebarNavigation } from './components/menu';
export { Flex } from './components/flex';
export { Form, FormField, FormContext, TextInput, Textarea, Checkbox, Select } from './components/form';
export { Grid, Row, Col } from './components/grid';
export { Heading } from './components/heading';
// export { BackgroundImage } from './components/image'
export { Layer } from './components/layer';
export { Link, BlockLink, ButtonLink } from './components/link';
export { List } from './components/list';
export { Logo, LogoMark } from './components/logo';
export { MotionBox } from './components/motion';
export { Popover } from './components/popover';
export { Section } from './components/section';
export { SegmentedControl } from './components/radio';
export { Slider } from './components/slider';
export { Spacer, SpaceChildren } from './components/Spacer';
export { Stat } from './components/stat';
export { Svg, IconSet, WeatherIcon } from './components/icon';
export { Table, TableOpts, DataTable, DataTableOpts, TableFilterStack, FilterTag, FilterTagList } from './components/table';
export { ZStack } from './components/stack';
export { Text } from './components/text';

export { CircularProgress } from './components/progress';
export { CircularMeter, LinearMeter } from './components/meter';
export { StationMarker } from './components/marker';

const Theme = createTheme({
    name: 'light',
    colors: {
        bg: {
            ...createColorScale(colors.brand.gray.levels),
            base: {
                primary: colors.white,
                secondary: colors.brand.gray.levels[1],
                tertiary: colors.brand.gray.levels[3]
            },
            reversed: {
                primary: colors.brand.gray.levels[13],
                secondary: colors.brand.gray.levels[11],
                tertiary: colors.brand.gray.levels[9]
            },
            surface: [
                colors.white,
                colors.brand.gray.levels[1],
                colors.brand.gray.levels[2],
                colors.brand.gray.levels[3],
                colors.brand.gray.levels[4],
                colors.brand.gray.levels[5]
            ]
        },
        text: {
            base: {
                primary: colors.text.dark.primary,
                secondary: colors.text.dark.secondary,
                tertiary: colors.text.dark.tertiary
            },
            reversed: {
                primary: colors.text.light.primary,
                secondary: colors.text.light.secondary,
                tertiary: colors.text.light.tertiary
            }
        }
    }
});

const DarkTheme = createTheme({
    name: 'dark',
    colors: {
        bg: {
            ...createColorScale([...colors.brand.gray.levels].reverse()),
            base: {
                primary: colors.brand.gray.base,
                secondary: colors.brand.gray.levels[12],
                tertiary: colors.brand.gray.levels[10]
            },
            reversed: {
                primary: colors.white,
                secondary: colors.brand.gray.levels[1],
                tertiary: colors.brand.gray.levels[3]
            },
            surface: [
                colors.brand.gray.base,
                colors.brand.gray.levels[13],
                colors.brand.gray.levels[12],
                colors.brand.gray.levels[11],
                colors.brand.gray.levels[10],
                colors.brand.gray.levels[9]
            ]
        },
        text: {
            base: {
                primary: colors.text.light.primary,
                secondary: colors.text.light.secondary,
                tertiary: colors.text.light.tertiary
            },
            reversed: {
                primary: colors.text.dark.primary,
                secondary: colors.text.dark.secondary,
                tertiary: colors.text.dark.tertiary
            },
            error: 'hsl(0,74%,59%)'
        }
    }
});

const ThemeContainer: React.FC<any> = (props: any): React.ReactElement => (
    <ThemeProvider theme={Theme}>
        <CSSReset />
        <GlobalStyle />
        {props.children}
    </ThemeProvider>
);

const CSSResetContainer = ({ children }: any): React.ReactElement => (
    <Box css={(theme: any) => css([reset(theme)])}>{children}</Box>
);

export {
    // ThemeProvider,
    Theme,
    DarkTheme,
    ThemeContainer,
    CSSResetContainer,
    GlobalStyle,
    defaultTheme
};
